import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import { serverInfo, serverInfoAuth } from "../http/ServerAPI";
import { purchaseShopItem } from "../http/shopAPI";
import BreadCrumbs from "../components/Breadcrumbs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ForumIcon from "@mui/icons-material/Forum";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { Context } from "../index";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area,
    ComposedChart, Legend
} from 'recharts';
import { observer } from "mobx-react-lite";
import {Modal, Card, CardContent, CardMedia, CardActions, Chip, TextField, MenuItem} from "@mui/material"; // Добавили Card компоненты
import { Refresh } from "@mui/icons-material";
import { userConnect, userInfo } from "../http/userAPI";
import { getWebSocketInstance } from "../http/wsUserAPI";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'; // Иконка для магазина
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group'; // Иконка для списка игроков

const getPlayersLabel = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return `${count} игрок`;
    if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) return `${count} игрока`;
    return `${count} игроков`;
};

const ServerDetails = observer(() => {
    const { user } = useContext(Context);
    const navigate = useNavigate();
    const { server_id } = useParams();
    const [server, setServer] = useState(null);
    const [steamName, setSteamName] = useState(null);
    const [usersInfo, setUsersInfo] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [isAuth, setIsAuth] = useState(false);
    const isMobile = useMediaQuery("(max-width:600px)");
    const [copySuccess, setCopySuccess] = useState(false);
    const [connectSuccess, setConnectSuccess] = useState(false);
    const [connectFail, setConnectFail] = useState(false);
    const [canAccessChat, setCanAccessChat] = useState(false);
    const [hasAccountConnection, setHasAccountConnection] = useState(false);
    const [showRating, setShowRating] = useState(false);
    const [showShop, setShowShop] = useState(false); // Состояние для отображения магазина
    const [selectedTab, setSelectedTab] = useState('top_scores');
    const [topUpModalOpen, setTopUpModalOpen] = useState(false);
    const [insufficientFundsItem, setInsufficientFundsItem] = useState(null); // Для хранения информации о товаре, на который не хватило средств
    const [playersModalOpen, setPlayersModalOpen] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [purchaseSnackbar, setPurchaseSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmPurchaseModalOpen, setConfirmPurchaseModalOpen] = useState(false);
    const [itemToPurchase, setItemToPurchase] = useState(null);
    const [shopCategory, setShopCategory] = useState("all");

    const scores = {
        top_scores: 'Топ 15 по общей статистике',
        top_explosives: 'Топ 15 рейдеров',
        top_resources: 'Топ 15 по фарму ресурсов',
        top_npc: 'Топ 15 убийц NPC',
        top_animal: 'Топ 15 охотников на зверей',
        top_times: 'Топ 15 по времени в игре',
    };

    const tabNames = {
        top_scores: 'Общий рейтинг',
        top_explosives: 'Рейдеры',
        top_resources: 'Фармеры',
        top_npc: 'Убийцы NPC',
        top_animal: 'Охотники',
        top_times: 'Время в игре',
    };

    const handleClosePurchaseSnackbar = () => {
        setPurchaseSnackbar({ ...purchaseSnackbar, open: false });
    };

    useEffect(() => {
        if (user.isAuth) {
            setIsAuth(true);
            setUsersInfo(user.userInfo);
        }
    }, [user.isAuth, user.userInfo]);

    useEffect(() => {
        setCanAccessChat(checkPermissions());
        setHasAccountConnection(isConnected());
    }, [usersInfo]);

    useEffect(() => {
        const fetchServerDetails = async () => {
            try {
                const data = user.isAuth ? await serverInfoAuth(server_id) : await serverInfo(server_id);
                setServer(data);
            } catch (error) {
                console.error("Failed to fetch server details:", error);
            }
        };
        fetchServerDetails();
    }, [server_id, user.isAuth]);

    const shopCategories = React.useMemo(() => {
        if (!server || !Array.isArray(server.shop_items)) return [];

        const set = new Set();

        server.shop_items.forEach(item => {
            if (item.category_name) {
                set.add(item.category_name);
            }
        });

        return Array.from(set);
    }, [server]);

    const filteredShopItems = React.useMemo(() => {
        if (!server || !Array.isArray(server.shop_items)) return [];

        if (shopCategory === "all") {
            return server.shop_items;
        }

        return server.shop_items.filter(
            item => item.category_name === shopCategory
        );
    }, [server, shopCategory]);

    const handleCopyToClipboard = () => {
        if (server) {
            navigator.clipboard.writeText(`${server.ip}:${server.port}`);
            setCopySuccess(true);
        }
    };

    const handleCloseSnackbar = () => {
        setCopySuccess(false);
        setConnectSuccess(false);
        setConnectFail(false);
    };

    const checkPermissions = () => {
        const perms = usersInfo?.user_perms;
        if (!perms) return false;

        const perm = perms[Number(server_id)];
        return perm?.r === 1;
    };

    const isConnected = () => {
        if (!usersInfo?.user_connect || usersInfo.user_connect.length === 0) {
            return false;
        }
        const connect = usersInfo.user_connect.find((item) => item.server_id === Number(server_id));
        if (connect && connect.steam_name) setSteamName(connect.steam_name);
        return usersInfo?.user_connect?.some((connect) => connect.server_id === Number(server_id));
    };

    const handleConfirmLink = async () => {
        try {
            console.log('Подтвердим данные ' + server_id);
            const data = await userConnect(server_id, 1);
            console.log(data);
            if (data.status) {
                setConnectSuccess(true);
                setModalOpen(false);
                const userData = await userInfo();
                console.log(userData);
                user.setUserInfo(userData);
                if (userData.name) user.setUserName(userData.name);
                // Переподключение WebSocket
                const webSocket = getWebSocketInstance(user);
                webSocket.reconnect(user);
            }
            // Обновите данные пользователя после подтверждения
        } catch (error) {
            console.error("Failed to confirm account link:", error);
        }
    };

    const handleCancelLink = async () => {
        try {
            console.log('Отменим подтверждение ' + server_id);
            userConnect(server_id, 0).then(data => {
                console.log(data);
                if (data.status) {
                    setConnectFail(true);
                    setModalOpen(false);
                    userInfo().then(data => {
                        console.log(data);
                        user.setUserInfo(data);
                    }).catch(e => {
                        console.log(e.response)
                    });
                }
            }).catch(e => {
                console.log(e.response)
            });
            //await cancelAccountLink(server_id);
            // Обновите данные пользователя после отмены
        } catch (error) {
            console.error("Failed to cancel account link:", error);
        }
    };

    const refresh = () => {
        userInfo().then(data => {
            console.log(data);
            user.setUserInfo(data)
        }).catch(e => {
            console.log(e.response)
        });
    }

    if (!server) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs='' name={server.name} />
            </Box>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <Paper sx={{ padding: 3, mb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'center' : 'flex-start',
                            textAlign: 'left',
                        }}
                    >
                        <Box
                            component="img"
                            src={server.img}
                            alt={`${server.name} logo`}
                            sx={{
                                width: isMobile ? '100%' : 200,
                                height: isMobile ? 'auto' : 200,
                                objectFit: isMobile ? "contain" : "cover",
                                borderRadius: 2,
                                marginBottom: isMobile ? 2 : 0,
                                marginRight: isMobile ? 0 : 3,
                                alignSelf: 'center',
                            }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="h2" gutterBottom>
                                {server.name}
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary"
                                        sx={{
                                            mt: 1,
                                            color: server.status === 3 ? "#89b6a1" : "#b98b97"
                                        }}
                            >
                                {server.status === 3 ? 'Онлайн' : 'Оффлайн'}: {getPlayersLabel(server.online)}
                            </Typography>

                            {/* Обновленный блок кнопок */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                {/* Кнопка Играть */}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    href={`steam://run/252490//+connect ${server.ip}:${server.port}`}
                                    target="_blank"
                                    title="Подключиться к серверу"
                                >
                                    Играть
                                </Button>

                                {/* Кнопка Копировать */}
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleCopyToClipboard}
                                    startIcon={<ContentCopyIcon />}
                                    title="Скопировать IP и порт"
                                >
                                    {isMobile ? "Копировать" : `${server.ip}:${server.port}`}
                                </Button>

                                {/* Кнопка Игроки онлайн - НАЧАЛО */}
                                {server.online_players && server.online_players.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => setPlayersModalOpen(true)}
                                        startIcon={<GroupIcon />} // Убедитесь, что GroupIcon импортирован
                                        title="Показать игроков онлайн"
                                    >
                                        Игроки ({server.online_players.length})
                                    </Button>
                                )}

                                {/* Кнопка Статистика */}
                                {server.xdStats && Object.keys(server.xdStats).length > 0 && (
                                    <Button
                                        variant={showRating ? "contained" : "outlined"}
                                        color="inherit"
                                        onClick={() => {
                                            setShowRating(prev => !prev);
                                            if (showShop) setShowShop(false); // Закрываем магазин, если открыт
                                        }}
                                        startIcon={<EmojiEventsIcon />}
                                        title="Показать статистику"
                                    >
                                        Статистика
                                    </Button>
                                )}

                                {/* Кнопка Магазин */}
                                {server.shop_items && server.shop_items.length > 0 && (
                                    <Button
                                        variant={showShop ? "contained" : "outlined"}
                                        color="inherit"
                                        onClick={() => {
                                            setShowShop(prev => !prev);
                                            if (showRating) setShowRating(false); // Закрываем статистику, если открыта
                                        }}
                                        startIcon={<ShoppingCartIcon />}
                                        title="Показать магазин сервера"
                                    >
                                        Магазин
                                    </Button>
                                )}

                                {/* Кнопка Чат */}
                                {isAuth && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            if (canAccessChat) {
                                                navigate(`/chat/${server.id}`);
                                            } else {
                                                setModalOpen(true);
                                            }
                                        }}
                                        startIcon={<ForumIcon />}
                                        color={canAccessChat ? "primary" : "inherit"}
                                        title={canAccessChat ? "Открыть чат" : "Свяжите аккаунт для доступа к чату"}
                                    >
                                        Чат
                                    </Button>
                                )}
                            </Box>

                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                                Добавлен: {server.created}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Блок с магазином */}
                    {showShop && server.shop_items && server.shop_items.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Магазин сервера
                            </Typography>
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                    label="Все"
                                    clickable
                                    color={shopCategory === "all" ? "primary" : "default"}
                                    variant={shopCategory === "all" ? "filled" : "outlined"}
                                    onClick={() => setShopCategory("all")}
                                />

                                {shopCategories.map(cat => (
                                    <Chip
                                        key={cat}
                                        label={cat}
                                        clickable
                                        color={shopCategory === cat ? "primary" : "default"}
                                        variant={shopCategory === cat ? "filled" : "outlined"}
                                        onClick={() => setShopCategory(cat)}
                                    />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {filteredShopItems.map((item) => {
                                    // Проверяем, достаточно ли средств
                                    const hasSufficientFunds = usersInfo?.balance !== undefined &&
                                        (parseFloat(usersInfo.balance ?? 0) + parseFloat(usersInfo.bonuses ?? 0)) >= parseFloat(item.price);

                                    return (
                                        <Card
                                            key={item.id}
                                            sx={{
                                                width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 12px)' },
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                            variant="outlined"
                                            elevation={2}
                                        >
                                            <CardMedia
                                                component="img"
                                                height="140"
                                                image={item.custom_image_url || item.image_url || '/storage/images/items/no_image.png'}
                                                alt={item.custom_name || item.name || 'Предмет'}
                                                sx={{
                                                    objectFit: 'contain',
                                                    pt: 2
                                                }}
                                                onError={(e) => {
                                                    e.target.src = '/storage/images/items/no_image.png';
                                                }}
                                            />
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography gutterBottom variant="h6" component="div">
                                                    {item.custom_name || item.name || `Предмет #${item.id}`}
                                                </Typography>
                                                {item.custom_description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ whiteSpace: "pre-line" }}
                                                    >
                                                        {item.custom_description}
                                                    </Typography>
                                                )}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                                    <Typography variant="h6" color="primary">
                                                        {item.price} ₽
                                                    </Typography>
                                                    <Chip
                                                        label={`x${item.stock}`}
                                                        size="small"
                                                        color={item.stock > 0 ? "success" : "error"}
                                                    />
                                                </Box>
                                            </CardContent>
                                            <CardActions>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    fullWidth
                                                    disabled={item.stock <= 0 || !isAuth || isPurchasing} // Добавили isPurchasing для блокировки во время запроса
                                                    color={!hasSufficientFunds && isAuth ? "inherit" : "primary"}
                                                    onClick={async () => { // Сделали обработчик асинхронным
                                                        if (!isAuth) {
                                                            // Можно добавить редирект на страницу входа или показать сообщение
                                                            // alert('Пожалуйста, войдите в систему для покупки.');
                                                            setPurchaseSnackbar({ open: true, message: 'Пожалуйста, войдите в систему для покупки.', severity: 'warning' });
                                                            return;
                                                        }
                                                        if (hasSufficientFunds) {
                                                            // Логика покупки
                                                            setItemToPurchase(item); // Сохраняем товар для покупки
                                                            setConfirmPurchaseModalOpen(true);
                                                        } else {
                                                            // Открываем модальное окно пополнения баланса
                                                            setInsufficientFundsItem(item);
                                                            setTopUpModalOpen(true);
                                                        }
                                                    }}
                                                >
                                                    {/* Отображение состояния кнопки */}
                                                    {isPurchasing ? (
                                                        <CircularProgress size={20} color="inherit" />
                                                    ) : item.stock <= 0 ? (
                                                        'Нет в наличии'
                                                    ) : !isAuth ? (
                                                        'Войдите для покупки'
                                                    ) : hasSufficientFunds ? (
                                                        'Купить'
                                                    ) : (
                                                        'Пополнить баланс'
                                                    )}
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}



                    {/* Блок описания и статистики */}
                    <Box sx={{ mt: 2 }}>
                        {/* Показываем описание, если ни статистика, ни магазин не открыты */}
                        {!showRating && !showShop && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {server.description.split("\n").map((line, index) => (
                                    <React.Fragment key={index}>
                                        {line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </Typography>
                        )}

                        {/* Блок со статистикой */}
                        {showRating && server.xdStats && Object.keys(server.xdStats).length > 0 && (
                            <Box>
                                <Typography variant="h6" component="h2" gutterBottom>
                                    Достижения игроков, обновляются несколько раз в сутки
                                </Typography>
                                <Tabs
                                    value={selectedTab}
                                    onChange={(event, newValue) => setSelectedTab(newValue)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ mb: 2 }}
                                >
                                    {Object.entries(tabNames).map(([key, label]) => (
                                        <Tab key={key} label={label} value={key} />
                                    ))}
                                </Tabs>
                                {Object.entries(server.xdStats).map(([key, entries]) => (
                                    selectedTab === key && (
                                        <Box key={key} sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                {scores[key]}
                                            </Typography>
                                            <Box
                                                component="table"
                                                sx={{
                                                    width: '100%',
                                                    borderCollapse: 'collapse',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    backgroundColor: 'background.paper',
                                                }}
                                            >
                                                <thead>
                                                <tr>
                                                    <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #444', width: '50%' }}>Игрок</th>
                                                    <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #444' }}>Score</th>
                                                    {'Bonus' in entries[0] && (
                                                        <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #444' }}>Бонусы</th>
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {entries.map((entry, i) => (
                                                    <tr
                                                        key={i}
                                                        style={{
                                                            backgroundColor: i % 2 === 0 ? '#464c4c' : '#525959',
                                                            transition: 'background-color 0.2s',
                                                        }}
                                                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#3f4949')}
                                                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#464c4c' : '#525959')}
                                                    >
                                                        <td style={{ padding: 8 }}>{entry.Name}</td>
                                                        <td style={{ padding: 8 }}>{entry.Score}</td>
                                                        {'Bonus' in entry && <td style={{ padding: 8 }}>{entry.Bonus > 0 ? entry.Bonus : ''}</td>}
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </Box>
                                        </Box>
                                    )
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Посещаемость сервера за 24 часа
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart data={server.player_online}>
                                <defs>
                                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tickFormatter={(date) => date.split(" ")[1]} />
                                <YAxis />
                                <Tooltip
                                    formatter={(value, name) => {
                                        if (name === "players") return [`${value}`, "Игроков"];
                                        if (name === "max_players") return [`${value}`, "Максимум игроков"];
                                        return value;
                                    }}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="players"
                                    stroke="#8884d8"
                                    fillOpacity={1}
                                    fill="url(#colorUv)"
                                    tooltipType="none"
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="players"
                                    name="Игроков"
                                    stroke="#8884d8"
                                    activeDot={{ r: 8 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="max_players"
                                    name="Максимум игроков"
                                    stroke="#82ca9d"
                                    strokeDasharray="5 5"
                                    dot={false}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            </Box>

            {/* Модальное окно подтверждения покупки */}
            <Modal open={confirmPurchaseModalOpen} onClose={() => setConfirmPurchaseModalOpen(false)}>
                <Paper sx={{
                    padding: 3,
                    maxWidth: 400,
                    width: '90%',
                    margin: "auto",
                    mt: "20vh",
                    backgroundColor: 'background.paper',
                    color: 'text.primary'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Подтверждение покупки</Typography>
                        <IconButton onClick={() => setConfirmPurchaseModalOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {itemToPurchase && (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Вы уверены, что хотите купить <strong>{itemToPurchase.custom_name || itemToPurchase.name || `Предмет #${itemToPurchase.id}`}</strong> - <strong>{itemToPurchase.stock} шт.</strong> за <strong>{itemToPurchase.price} ₽</strong>?
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={async () => {
                                        // Логика фактической покупки
                                        setIsPurchasing(true); // Начинаем процесс покупки
                                        setConfirmPurchaseModalOpen(false); // Закрываем модалку подтверждения
                                        try {
                                            const purchaseResult = await purchaseShopItem(server.id, itemToPurchase.id); // Вызываем API
                                            console.log("Результат покупки:", purchaseResult);
                                            if (purchaseResult && purchaseResult.status === true) {
                                                // Успешная покупка
                                                setPurchaseSnackbar({
                                                    open: true,
                                                    message: (
                                                        <>
                                                            {purchaseResult.message || `Покупка ${itemToPurchase.custom_name || itemToPurchase.name || `Предмет #${itemToPurchase.id}`} - ${itemToPurchase.stock} шт. прошла успешно!`}
                                                            <br />Заберите в игре, команда /shop
                                                        </>
                                                    ),
                                                    severity: 'success'
                                                });
                                                // TODO: Обновить баланс пользователя и/или количество товара на складе
                                            } else {
                                                // Ошибка покупки
                                                setPurchaseSnackbar({
                                                    open: true,
                                                    message: purchaseResult?.message || `Ошибка покупки ${itemToPurchase.custom_name || itemToPurchase.name || `Предмет #${itemToPurchase.id}`}`,
                                                    severity: 'error'
                                                });
                                            }
                                        } catch (error) {
                                            console.error("Ошибка сети или сервера при покупке:", error);
                                            setPurchaseSnackbar({
                                                open: true,
                                                message: "Произошла ошибка при попытке покупки. Пожалуйста, попробуйте позже.",
                                                severity: 'error'
                                            });
                                        } finally {
                                            setIsPurchasing(false); // Завершаем процесс покупки в любом случае
                                            setItemToPurchase(null); // Очищаем выбранный товар
                                        }
                                    }}
                                >
                                    Подтвердить
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        setConfirmPurchaseModalOpen(false);
                                        setItemToPurchase(null);
                                    }}
                                >
                                    Отмена
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Modal>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Paper sx={{ padding: 3, maxWidth: 400, margin: "auto", mt: "20vh" }}>
                    <Typography variant="h6">Свяжите аккаунт с игрой</Typography>
                    <Typography variant="body2">
                        Введите в игре команду: <strong>/connect {usersInfo?.login}</strong>
                    </Typography>
                    <Typography variant="body2">
                        Затем подтвердите привязку {steamName} в этом окне.
                    </Typography>
                    {hasAccountConnection && (
                        <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
                            <Button variant="contained" color="primary" onClick={handleConfirmLink}>
                                Подтвердить
                            </Button>
                            <Button variant="contained" color="error" onClick={handleCancelLink}>
                                Отменить
                            </Button>
                        </Box>
                    )}
                    {!hasAccountConnection && (
                        <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
                            <Typography variant="body2" style={{ lineHeight: "50px" }}>
                                Нажмите обновить, если ввели команду:
                            </Typography>
                            <IconButton>
                                <Refresh
                                    color="primary"
                                    fontSize="large"
                                    title="Обновить"
                                    style={{ cursor: 'pointer' }}
                                    onClick={refresh}
                                />
                            </IconButton>
                        </Box>
                    )}
                    <Button variant="contained" fullWidth onClick={() => setModalOpen(false)}>
                        Закрыть
                    </Button>
                </Paper>
            </Modal>

            <Modal open={topUpModalOpen} onClose={() => setTopUpModalOpen(false)}>
                <Paper sx={{
                    padding: 3,
                    maxWidth: 400,
                    width: '90%',
                    margin: "auto",
                    mt: "20vh",
                    backgroundColor: 'background.paper',
                    color: 'text.primary'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Пополнение баланса</Typography>
                        <IconButton onClick={() => setTopUpModalOpen(false)} size="small">
                            <CloseIcon /> {/* Убедитесь, что CloseIcon импортирован */}
                        </IconButton>
                    </Box>

                    {insufficientFundsItem && (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Для покупки <strong>{insufficientFundsItem.custom_name || insufficientFundsItem.name || `Предмет #${insufficientFundsItem.id}`}</strong> за <strong>{insufficientFundsItem.price} ₽</strong> необходимо пополнить баланс.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Ваш текущий баланс: <strong>{usersInfo?.balance !== undefined ? `${usersInfo.balance} ₽` : 'Неизвестно'}</strong>
                            </Typography>
                            <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                                Недостаточно средств: <strong>{insufficientFundsItem.price - (usersInfo?.balance || 0)} ₽</strong>
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                            // Здесь можно добавить навигацию на страницу пополнения баланса
                            navigate('/wallet'); // Предполагаем, что у вас есть маршрут /wallet
                            setTopUpModalOpen(false);
                        }}
                    >
                        Перейти к пополнению баланса
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 1 }}
                        onClick={() => setTopUpModalOpen(false)}
                    >
                        Закрыть
                    </Button>
                </Paper>
            </Modal>

            <Snackbar
                open={connectSuccess}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    Аккаунты успешно связаны
                </Alert>
            </Snackbar>

            <Snackbar
                open={connectFail}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    Вы отменили привязку аккаунта
                </Alert>
            </Snackbar>

            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    IP и порт скопированы в буфер обмена!
                </Alert>
            </Snackbar>

            <Snackbar
                open={purchaseSnackbar.open}
                autoHideDuration={6000} // Можно настроить длительность
                onClose={handleClosePurchaseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleClosePurchaseSnackbar}
                    severity={purchaseSnackbar.severity} // 'success', 'error', 'warning', 'info'
                    sx={{ width: '100%' }}
                >
                    {purchaseSnackbar.message}
                </Alert>
            </Snackbar>

            {/* Модальное окно для отображения игроков онлайн - НАЧАЛО */}
            <Modal open={playersModalOpen} onClose={() => setPlayersModalOpen(false)}>
                <Paper sx={{
                    padding: 3,
                    maxWidth: 500,
                    width: '90%',
                    margin: "auto",
                    mt: "10vh",
                    maxHeight: '80vh',
                    overflow: 'auto',
                    backgroundColor: 'background.paper',
                    color: 'text.primary'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Игроки онлайн ({server.online_players?.length || 0})</Typography>
                        <IconButton onClick={() => setPlayersModalOpen(false)} size="small">
                            <CloseIcon /> {/* Убедитесь, что CloseIcon импортирован */}
                        </IconButton>
                    </Box>
                    {server.online_players && server.online_players.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {server.online_players.map((player, index) => (
                                <Chip
                                    key={index}
                                    label={player.name}
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: 'action.hover',
                                        borderColor: 'divider',
                                        color: 'text.primary'
                                    }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography>На сервере нет игроков онлайн</Typography>
                    )}
                </Paper>
            </Modal>
        </Container>
    );
});

export default ServerDetails;